// @ts-nocheck
// TODO: tighten types after migration
import React, { useState, useEffect, useRef, useCallback } from 'react';
import './ListenToArticle.css';

const ListenToArticle = () => {
    const [isSupported, setIsSupported] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isPaused, setIsPaused] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [speed, setSpeed] = useState(1);
    const [voices, setVoices] = useState([]);
    const [selectedVoice, setSelectedVoice] = useState(null);
    const [progress, setProgress] = useState(0);
    const [isExpanded, setIsExpanded] = useState(false);

    // ...
    const synthRef = useRef(window.speechSynthesis);
    const utteranceRef = useRef(null);
    const chunksRef = useRef([]);
    const indexRef = useRef(0);
    const isPausedRef = useRef(false);
    const settingsRef = useRef(null);

    // Initial check and voice loading
    useEffect(() => {
        if ('speechSynthesis' in window) {
            setIsSupported(true);
            
            const loadVoices = () => {
                const availableVoices = synthRef.current.getVoices();
                setVoices(availableVoices);
                // Prefer English voices, default to first available
                const defaultVoice = availableVoices.find(v => v.lang.startsWith('en')) || availableVoices[0];
                setSelectedVoice(defaultVoice);
            };

            loadVoices();
            
            // Chrome loads voices asynchronously
            if (synthRef.current.onvoiceschanged !== undefined) {
                synthRef.current.onvoiceschanged = loadVoices;
            }
        }
    }, []);

    // Cleanup on unmount, visibility change, or navigation
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.hidden) {
                stopSpeech('visibility hidden');
            }
        };

        const handleBeforeUnload = () => {
             stopSpeech('before unload');
        };

        const handleClickOutside = (event) => {
            if (settingsRef.current && !settingsRef.current.contains(event.target)) {
                setShowSettings(false);
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('beforeunload', handleBeforeUnload);
        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            stopSpeech();
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('beforeunload', handleBeforeUnload);
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const stopSpeech = (reason = 'unknown') => {
        console.log(`[ListenToArticle] stopSpeech called. Reason: ${reason}`);
        if (synthRef.current) {
            synthRef.current.cancel();
        }
        setIsPlaying(false);
        setIsPaused(false);
        isPausedRef.current = false;
        indexRef.current = 0;
        setProgress(0);
    };

    const getTextContent = () => {
        const article = document.getElementById('blog-content');
        if (!article) {
             console.warn('ListenToArticle: #blog-content element not found');
             return '';
        }
        
        // Use innerText to respect CSS visibility/styling, or textContent for raw text
        // innerText is usually better for "reading" as it skips hidden elements
        const text = article.innerText || '';
        console.log(`ListenToArticle: Found content with length ${text.length}`);
        return text;
    };

    const chunkText = (text) => {
        // Simple cleaner chunking
        const paragraphs = text.split(/\n+/);
        const chunks = [];
        
        paragraphs.forEach(para => {
            const trimmed = para.trim();
            if (trimmed.length > 0) {
                if (trimmed.length > 200) {
                   // Split by sentences if too long
                   const sentences = trimmed.match(/[^.!?]+[.!?]+|[^.!?]+$/g) || [trimmed];
                   chunks.push(...sentences.map(s => s.trim()).filter(s => s.length > 0));
                } else {
                   chunks.push(trimmed);
                }
            }
        });
        return chunks;
    };

    const speakChunk = useCallback((index) => {
        if (index >= chunksRef.current.length) {
            stopSpeech(); // Finished
            return;
        }

        const chunk = chunksRef.current[index];
        console.log(`ListenToArticle: Speaking chunk ${index + 1}/${chunksRef.current.length}: ${chunk.substring(0, 20)}...`);

        // Cancel previous before speaking new one (critical for consistency)
        synthRef.current.cancel();

        const utterance = new SpeechSynthesisUtterance(chunk);
        
        if (selectedVoice) {
            utterance.voice = selectedVoice;
        }
        utterance.rate = speed;
        
        utterance.onend = () => {
            if (!isPausedRef.current && isPlaying) {
                const nextIndex = index + 1;
                indexRef.current = nextIndex;
                updateProgress(nextIndex);
                speakChunk(nextIndex);
            }
        };

        utterance.onerror = (e) => {
            console.error('Speech synthesis error:', e);
            if (e.error !== 'interrupted' && e.error !== 'canceled') {
                 const nextIndex = index + 1;
                 indexRef.current = nextIndex;
                  updateProgress(nextIndex);
                 speakChunk(nextIndex);
            }
        };

        utteranceRef.current = utterance;
        synthRef.current.speak(utterance);
    }, [selectedVoice, speed, isPlaying]);

    const updateProgress = (index) => {
        const total = chunksRef.current.length || 1;
        const pct = Math.min(100, Math.round((index / total) * 100));
        setProgress(pct);
    };

    // Effect to restart speech if voice or speed changes WHILE playing
    const restartCurrentChunk = () => {
         console.log(`[ListenToArticle] restartCurrentChunk. isPlaying=${isPlaying}, isPaused=${isPaused}, index=${indexRef.current}`);
         if (isPlaying && !isPaused) {
             // Just triggering speakChunk again with current index will pick up new settings
             speakChunk(indexRef.current);
         }
    };
    
    // Watch for speed/voice changes
    const initialRender = useRef(true);
    useEffect(() => {
        if (initialRender.current) {
            initialRender.current = false;
            return;
        }
        if (isPlaying && !isPaused) {
            restartCurrentChunk();
        }
    }, [speed, selectedVoice]); // eslint-disable-line react-hooks/exhaustive-deps


    const handlePlayPause = () => {
        if (isPlaying) {
            if (isPaused) {
                // Resume
                synthRef.current.resume();
                setIsPaused(false);
                isPausedRef.current = false;
                // Sometimes resume doesn't work if it was cancelled or timed out
                if (!synthRef.current.speaking) {
                    speakChunk(indexRef.current);
                }
            } else {
                // Pause
                synthRef.current.pause();
                setIsPaused(true);
                isPausedRef.current = true;
            }
        } else {
            // Start
            const text = getTextContent();
            if (!text) {
                alert('No content found to read.');
                return;
            }
            const chunks = chunkText(text);
            console.log(`ListenToArticle: Prepared ${chunks.length} chunks`);
            
            if (chunks.length === 0) {
                 alert('Content is empty.');
                 return;
            }

            chunksRef.current = chunks;
            indexRef.current = 0;
            updateProgress(0);
            
            setIsPlaying(true);
            setIsPaused(false);
            isPausedRef.current = false;
            
            speakChunk(0);
        }
    };

    const handleStop = () => {
        stopSpeech();
    };
    
    const toggleSettings = () => {
        setShowSettings(!showSettings);
    };

    const handleSpeedChange = (newSpeed) => {
        console.log(`[ListenToArticle] handleSpeedChange: ${newSpeed}`);
        setSpeed(newSpeed);
    };

    const handleVoiceChange = (e) => {
        const voiceName = e.target.value;
        const voice = voices.find(v => v.name === voiceName);
        setSelectedVoice(voice);
    };

    if (!isSupported) return null;

    return (
        <div className="listen-article-wrapper flex justify-start">
            {!showSettings && !isPlaying && !isPaused && !isSupported ? null : ( // Keeping existing supported check logic if needed, but logic below handles isSupported return
             <>   
            {!isExpanded ? (
                <div className="listen-article-container collapsed flex w-fit cursor-pointer items-center gap-[12px] rounded-[50px] border border-[#e9ecef] bg-[#f8f9fa] px-[16px] py-[6px] shadow-[0_2px_4px_rgba(0,0,0,0.02)] transition-all duration-300" onClick={() => {
                    setIsExpanded(true);
                    setTimeout(() => handlePlayPause(), 100);
                }} style={{cursor: 'pointer'}}>
                     <button className="listen-btn-icon flex items-center justify-center rounded-full border-none bg-none p-[6px] text-[1.2rem] leading-[0] text-[#444] transition-all duration-200 hover:bg-black/5 hover:text-black">
                        <i className="fi fi-sr-headset"></i>
                    </button>
                    <div className="listen-status-text flex items-center whitespace-nowrap text-[0.95rem] font-[500] text-[#444]">
                        <span>Listen</span>
                    </div>
                </div>
            ) : (
            <div className="listen-article-container flex w-fit items-center gap-[12px] rounded-[50px] border border-[#e9ecef] bg-[#f8f9fa] px-[16px] py-[6px] shadow-[0_2px_4px_rgba(0,0,0,0.02)] transition-all duration-300">
                {/* Controls Group */}
                <div className="listen-controls-group flex items-center gap-[8px]">
                    {/* Play/Pause Button */}
                    <button 
                        className={`listen-btn-icon flex items-center justify-center rounded-full border-none bg-none p-[6px] text-[1.2rem] leading-[0] text-[#444] transition-all duration-200 hover:bg-black/5 hover:text-black ${isPlaying ? 'active text-[#47b5ff]' : ''}`} 
                        onClick={handlePlayPause}
                        title={isPlaying && !isPaused ? "Pause" : "Listen to this Article"}
                    >
                        {isPlaying && !isPaused ? (
                            <i className="fi fi-sr-pause"></i>
                        ) : (
                            <i className="fi fi-sr-play"></i>
                        )}
                    </button>

                    {/* Stop Button - Only show when playing/paused */}
                    {(isPlaying || isPaused) && (
                         <button 
                            className="listen-btn-icon stop-btn flex items-center justify-center rounded-full border-none bg-none p-[6px] text-[1.2rem] leading-[0] text-[#dc3545] transition-all duration-200 hover:bg-[rgba(220,53,69,0.1)]" 
                            onClick={handleStop}
                            title="Stop"
                        >
                            <i className="fi fi-br-stop-circle"></i>
                        </button>
                    )}
                </div>

                {/* Vertical Separator */}
                <div className="listen-separator h-[24px] w-[1px] bg-[#dee2e6]"></div>

                {/* Settings Toggle */}
                <div className="listen-settings-wrapper relative flex items-center" ref={settingsRef}>
                    <button 
                        className={`listen-btn-icon settings-btn flex items-center justify-center rounded-full border-none bg-none p-[6px] text-[1.2rem] leading-[0] text-[#444] transition-all duration-200 hover:bg-black/5 hover:text-black ${showSettings ? 'active text-[#47b5ff]' : ''}`}
                        onClick={toggleSettings}
                        title="Voice & Speed Settings"
                    >
                        <i className="fi fi-br-settings-sliders"></i>
                    </button>

                    {/* Settings Popup */}
                    {showSettings && (
                        <div className="listen-settings-popup absolute left-1/2 top-full z-[1000] mt-[15px] min-w-[200px] -translate-x-1/2 rounded-[12px] border border-[#ddd] bg-white p-[15px] font-['Outfit',sans-serif] shadow-[0_4px_12px_rgba(0,0,0,0.15)]">
                            <div className="listen-setting-row mb-[12px] flex flex-col gap-[6px]">
                                <label className="text-[0.8rem] font-[600] uppercase tracking-[0.5px] text-[#666]">Speed</label>
                                <div className="listen-speed-options flex gap-[2px] rounded-[8px] bg-[#f1f3f5] p-[3px]">
                                    <button 
                                        className={`flex-1 rounded-[6px] border-none bg-transparent px-0 py-[6px] text-[0.85rem] text-[#555] transition-all duration-200 hover:bg-white/50 ${speed === 0.75 ? 'active bg-white font-[600] text-black shadow-[0_1px_2px_rgba(0,0,0,0.1)]' : ''}`}
                                        onClick={() => handleSpeedChange(0.75)}
                                    >0.75x</button>
                                    <button 
                                        className={`flex-1 rounded-[6px] border-none bg-transparent px-0 py-[6px] text-[0.85rem] text-[#555] transition-all duration-200 hover:bg-white/50 ${speed === 1 ? 'active bg-white font-[600] text-black shadow-[0_1px_2px_rgba(0,0,0,0.1)]' : ''}`}
                                        onClick={() => handleSpeedChange(1)}
                                    >1.0x</button>
                                    <button 
                                        className={`flex-1 rounded-[6px] border-none bg-transparent px-0 py-[6px] text-[0.85rem] text-[#555] transition-all duration-200 hover:bg-white/50 ${speed === 1.25 ? 'active bg-white font-[600] text-black shadow-[0_1px_2px_rgba(0,0,0,0.1)]' : ''}`}
                                        onClick={() => handleSpeedChange(1.25)}
                                    >1.25x</button>
                                </div>
                            </div>
                            
                            {voices.length > 0 && (
                                <div className="listen-setting-row flex flex-col gap-[6px]">
                                    <label className="text-[0.8rem] font-[600] uppercase tracking-[0.5px] text-[#666]">Voice</label>
                                    <select 
                                        value={(selectedVoice && selectedVoice.name) || ''} 
                                        onChange={handleVoiceChange}
                                        className="w-full rounded-[8px] border border-[#ddd] bg-white p-[8px] text-[0.9rem] outline-none"
                                    >
                                        {voices.map(v => (
                                            <option key={v.name} value={v.name}>
                                                {v.name.slice(0, 25)}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Status Text */}
                <div className="listen-status-text flex items-center whitespace-nowrap text-[0.95rem] font-[500] text-[#444]">
                    {isPlaying ? (
                        <span className="listen-progress-text font-[600] text-text">
                            {progress}% Played
                        </span>
                    ) : (
                        <span>Listen to this Article</span>
                    )}
                </div>
            </div>
            )}
            </>
            )}
        </div>
    );
};

export default ListenToArticle;

